import type { BoardNode } from '@shared/board/types';
import type { Vec2 } from './input/types';

export interface HorizontalVector {
  readonly x: number;
  readonly z: number;
}

export interface WarmupArenaDirectionalTargetArgs {
  readonly nodes: readonly BoardNode[];
  readonly currentNodeId: string;
  readonly arenaIslandId: string;
  readonly input: Vec2;
  readonly cameraForward: HorizontalVector;
  readonly cameraRight: HorizontalVector;
}

const MIN_INPUT_MAGNITUDE = 0.25;
const MIN_DIRECTION_ALIGNMENT = 0.35;

function normalizeHorizontal(vector: HorizontalVector): HorizontalVector | null {
  const length = Math.hypot(vector.x, vector.z);
  if (length < 0.0001) return null;
  return { x: vector.x / length, z: vector.z / length };
}

function nodePosition(node: BoardNode): HorizontalVector {
  return { x: node.position.x, z: node.position.z };
}

export function pickWarmupArenaDirectionalTarget(
  args: WarmupArenaDirectionalTargetArgs,
): string | null {
  const inputLength = Math.hypot(args.input.x, args.input.y);
  if (inputLength < MIN_INPUT_MAGNITUDE) return null;

  const currentNode = args.nodes.find((node) => node.id === args.currentNodeId);
  if (currentNode == null) return null;

  const cameraForward = normalizeHorizontal(args.cameraForward) ?? { x: 0, z: -1 };
  const cameraRight = normalizeHorizontal(args.cameraRight) ?? { x: 1, z: 0 };
  const input = { x: args.input.x / inputLength, y: args.input.y / inputLength };
  const desiredDirection = normalizeHorizontal({
    x: cameraRight.x * input.x + cameraForward.x * input.y,
    z: cameraRight.z * input.x + cameraForward.z * input.y,
  });
  if (desiredDirection == null) return null;

  const current = nodePosition(currentNode);
  const arenaPrefix = `${args.arenaIslandId}-`;
  let best: {
    nodeId: string;
    score: number;
  } | null = null;

  for (const candidate of args.nodes) {
    if (candidate.id === currentNode.id) continue;
    if (candidate.type === 'pathing') continue;
    if (!candidate.id.startsWith(arenaPrefix)) continue;

    const position = nodePosition(candidate);
    const delta = { x: position.x - current.x, z: position.z - current.z };
    const distance = Math.hypot(delta.x, delta.z);
    if (distance < 0.0001) continue;

    const alignment =
      (delta.x / distance) * desiredDirection.x + (delta.z / distance) * desiredDirection.z;
    if (alignment < MIN_DIRECTION_ALIGNMENT) continue;

    const forwardDistance = delta.x * desiredDirection.x + delta.z * desiredDirection.z;
    const perpendicularDistance = Math.sqrt(
      Math.max(0, distance * distance - forwardDistance * forwardDistance),
    );
    const score = perpendicularDistance * 1.3 + forwardDistance * 0.12 + (1 - alignment) * 0.8;

    if (best == null || score < best.score) {
      best = { nodeId: candidate.id, score };
    }
  }

  return best?.nodeId ?? null;
}
